// Settings → Platform → Notifications. Modal editor for one email template:
// WYSIWYG body, clickable placeholders, who-gets-it helper, live preview and a
// test send to whichever addresses you choose. Templates are never deleted —
// each one is wired to a feature — they can only be switched off.
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Mail, RotateCcw, Save, Send, Info, Eye, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/RichTextEditor";

const FIELDS = ["subject", "heading", "body_html", "cta_label", "org_default"];
const draftOf = (t) => ({
  subject: t.subject || "", heading: t.heading || "", body_html: t.body_html || "",
  cta_label: t.cta_label || "", org_default: t.org_default || "", enabled: t.enabled !== false,
});

export function EmailTemplateEditor({ template, canEdit, myEmail, syntaxHelp = [], onClose, onSaved }) {
  const [draft, setDraft] = useState(() => draftOf(template));
  const [preview, setPreview] = useState({ subject: "", html: "" });
  const [rendering, setRendering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState(myEmail || "");
  const focusRef = useRef("body_html");
  const subjectRef = useRef(null);
  const headingRef = useRef(null);
  const bodyWrapRef = useRef(null);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const payload = useCallback(() => {
    const out = {};
    FIELDS.forEach((f) => { out[f] = draft[f]; });
    return out;
  }, [draft]);

  useEffect(() => {
    const id = setTimeout(async () => {
      setRendering(true);
      try {
        const { data } = await api.post(`/settings/email-templates/${template.id}/render`, payload());
        setPreview(data);
      } catch { /* preview is best-effort */ }
      finally { setRendering(false); }
    }, 500);
    return () => clearTimeout(id);
  }, [payload, template.id]);

  const insert = (token) => {
    const chunk = `{{${token}}}`;
    const target = focusRef.current;
    if (target === "body_html") {
      const el = bodyWrapRef.current?.querySelector('[contenteditable="true"]');
      if (el) {
        el.focus();
        document.execCommand("insertText", false, chunk);
        set("body_html", el.innerHTML);
        return;
      }
    }
    const ref = target === "heading" ? headingRef : subjectRef;
    const el = ref.current;
    if (!el) { set(target, `${draft[target] || ""}${chunk}`); return; }
    const start = el.selectionStart ?? (draft[target] || "").length;
    const next = `${(draft[target] || "").slice(0, start)}${chunk}${(draft[target] || "").slice(el.selectionEnd ?? start)}`;
    set(target, next);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + chunk.length, start + chunk.length); });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/settings/email-templates/${template.id}`, { ...payload(), enabled: draft.enabled });
      toast.success(`“${data.label}” saved`);
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Restore the shipped copy for this template? Your edits are discarded.")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/settings/email-templates/${template.id}/reset`);
      setDraft(draftOf(data));
      onSaved?.(data);
      toast.success("Back to the default copy");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset");
    } finally { setSaving(false); }
  };

  const sendPreview = async () => {
    setSending(true);
    try {
      const recipients = to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post(`/settings/email-templates/${template.id}/send-preview`,
        { ...payload(), recipients });
      toast.success(`Preview sent to ${data.recipients.join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the preview");
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[94vw] xl:max-w-[1200px] max-h-[92vh] overflow-y-auto"
        data-testid="email-template-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-primary" /> {template.label}
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800">
              {template.group}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-sky-200 bg-sky-50/70 px-3 py-2 text-xs text-sky-900 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200"
          data-testid="email-template-audience">
          <div className="flex items-start gap-2">
            <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>
              <b>Who gets this:</b> {template.audience}
              <div className="mt-0.5 opacity-80"><b>When:</b> {template.sends_when}</div>
            </div>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,540px)]">
          {/* ------------------------------------------------ editor */}
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700">
              <div>
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Template active</div>
                <div className="text-xs text-slate-400">Switch off and this email is never sent — the feature keeps working.</div>
              </div>
              <Switch checked={draft.enabled} disabled={!canEdit}
                onCheckedChange={(v) => set("enabled", v)}
                data-testid="email-template-editor-enabled" />
            </div>

            <div>
              <Label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Subject</Label>
              <Input ref={subjectRef} value={draft.subject} disabled={!canEdit}
                onFocus={() => { focusRef.current = "subject"; }}
                onChange={(e) => set("subject", e.target.value)}
                className="mt-1 h-9 text-sm" data-testid="email-template-subject" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Heading in the email</Label>
                <Input ref={headingRef} value={draft.heading} disabled={!canEdit}
                  onFocus={() => { focusRef.current = "heading"; }}
                  onChange={(e) => set("heading", e.target.value)}
                  className="mt-1 h-9 text-sm" data-testid="email-template-heading" />
              </div>
              <div>
                <Label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Button label</Label>
                <Input value={draft.cta_label} disabled={!canEdit}
                  placeholder="No button on this email"
                  onChange={(e) => set("cta_label", e.target.value)}
                  className="mt-1 h-9 text-sm" data-testid="email-template-cta" />
              </div>
            </div>

            <div ref={bodyWrapRef} onFocus={() => { focusRef.current = "body_html"; }}>
              <Label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Body</Label>
              <div className="mt-1">
                <RichTextEditor value={draft.body_html} disabled={!canEdit}
                  onChange={(v) => set("body_html", v)}
                  placeholder="Write the email copy…" testid="email-template-body" />
              </div>
            </div>

            <div>
              <Label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Placeholders — click to insert
              </Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="email-template-placeholders">
                {(template.placeholders || []).map((p) => (
                  <button key={p.token} type="button" disabled={!canEdit}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insert(p.token)}
                    title={`${p.label}${p.help ? ` — ${p.help}` : ""}`}
                    data-testid={`email-template-placeholder-${p.token}`}
                    className="rounded-full border border-slate-200 bg-white px-2 py-0.5 font-mono text-[11px] text-slate-600 transition-colors hover:border-primary/50 hover:text-primary disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                    {`{{${p.token}}}`}
                  </button>
                ))}
              </div>
              <ul className="mt-2 space-y-0.5 text-[11px] leading-relaxed text-slate-400">
                {(template.placeholders || []).filter((p) => p.help).map((p) => (
                  <li key={`h-${p.token}`}><span className="font-mono text-slate-500">{`{{${p.token}}}`}</span> — {p.help}</li>
                ))}
                {syntaxHelp.map((h) => <li key={h} className="flex gap-1"><Info className="mt-0.5 h-3 w-3 shrink-0" />{h}</li>)}
              </ul>
            </div>

            <div>
              <Label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Organization name — default when we are the sender
              </Label>
              <Input value={draft.org_default} disabled={!canEdit}
                onChange={(e) => set("org_default", e.target.value)}
                className="mt-1 h-9 max-w-xs text-sm" data-testid="email-template-org-default" />
              <p className="mt-1 text-[11px] text-slate-400">
                Used for <span className="font-mono">{"{{organization}}"}</span> and the email&rsquo;s brand line. A partner
                sending their own mail replaces it with their business name.
              </p>
            </div>
          </div>

          {/* ------------------------------------------------ preview */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400">
              <Eye className="h-3.5 w-3.5" /> Preview with sample data
              {rendering && <Loader2 className="h-3 w-3 animate-spin" />}
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              data-testid="email-template-preview-subject">
              <span className="text-slate-400">Subject:&nbsp;</span>{preview.subject || "—"}
            </div>
            <iframe title="Email preview" srcDoc={preview.html || ""} sandbox=""
              className="h-[46vh] w-full rounded-md border border-slate-200 bg-white dark:border-slate-700"
              data-testid="email-template-preview-frame" />
            <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <Label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Send a preview to</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={to} onChange={(e) => setTo(e.target.value)}
                  placeholder="you@company.ca, qa@company.ca"
                  className="h-9 text-sm" data-testid="email-template-preview-to" />
                <Button onClick={sendPreview} disabled={!canEdit || sending || !to.trim()}
                  className="h-9 shrink-0 gap-1.5" data-testid="email-template-send-preview">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                Sends exactly what you see, with sample data and a [PREVIEW] subject prefix.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3 dark:border-slate-800">
          <Button variant="ghost" onClick={reset} disabled={!canEdit || saving}
            className="gap-1.5 text-slate-500" data-testid="email-template-reset">
            <RotateCcw className="h-3.5 w-3.5" /> Restore default copy
          </Button>
          <div className="flex items-center gap-2">
            {template.updated_by_name && (
              <span className="text-[11px] text-slate-400">last edited by {template.updated_by_name}</span>
            )}
            <Button variant="outline" onClick={onClose} data-testid="email-template-cancel">Cancel</Button>
            <Button onClick={save} disabled={!canEdit || saving} className="gap-1.5" data-testid="email-template-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EmailTemplateEditor;
